import { useEffect, useState } from "react";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import {
  CALCULATORS_CMS_DOC,
  defaultCalculatorsCms,
  mergeCalculatorsCms,
} from "../../lib/calculatorsCms";

const PROTOCOL_KEYS = [
  { id: "niosh", label: "NIOSH / ACGIH" },
  { id: "eu", label: "ISO / Europa / ES" },
  { id: "osha", label: "OSHA / MSHA" },
];

export default function CmsCalculatorsEditor() {
  const { data, loading, source } = useCmsDoc(
    CALCULATORS_CMS_DOC.collection,
    CALCULATORS_CMS_DOC.id,
    defaultCalculatorsCms()
  );
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    setForm(mergeCalculatorsCms(data));
  }, [data]);

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando Calculadoras…</p>;
  }

  const setPage = (patch) => setForm((prev) => ({ ...prev, page: { ...prev.page, ...patch } }));
  const setDiag = (patch) =>
    setForm((prev) => ({
      ...prev,
      ruido: { ...prev.ruido, diagnostics: { ...prev.ruido.diagnostics, ...patch } },
    }));
  const setProtocol = (key, patch) =>
    setForm((prev) => ({
      ...prev,
      ruido: {
        ...prev.ruido,
        protocols: {
          ...prev.ruido.protocols,
          [key]: { ...prev.ruido.protocols[key], ...patch },
        },
      },
    }));
  const setCalorRisk = (patch) =>
    setForm((prev) => ({
      ...prev,
      calor: { ...prev.calor, riskLabels: { ...prev.calor.riskLabels, ...patch } },
    }));

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = mergeCalculatorsCms(form);
      await saveCmsDoc(CALCULATORS_CMS_DOC.collection, CALCULATORS_CMS_DOC.id, payload);
      setForm(payload);
      setMessage("Calculadoras guardadas.");
    } catch (err) {
      setError(err?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h2 className="font-display text-3xl font-black">Calculadoras</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Textos de la página pública y diagnósticos técnicos. Las fórmulas y límites numéricos
          siguen en código.
        </p>
        <p className="text-[11px] text-brand-dark/40 mt-1">
          Fuente: {source === "firestore" ? "Firestore" : "valores por defecto"}
        </p>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      {message ? <p className="text-sm text-brand-primary font-medium">{message}</p> : null}

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Página /calculadoras</h3>
        <Field label="Título">
          <input
            className={inputClass}
            value={form.page.title}
            onChange={(e) => setPage({ title: e.target.value })}
          />
        </Field>
        <Field label="Destacado (color marca)">
          <input
            className={inputClass}
            value={form.page.titleHighlight}
            onChange={(e) => setPage({ titleHighlight: e.target.value })}
          />
        </Field>
        <Field label="Subtítulo">
          <textarea
            className={textareaClass}
            value={form.page.subtitle}
            onChange={(e) => setPage({ subtitle: e.target.value })}
          />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Ruido — diagnósticos</h3>
        <p className="text-[11px] text-brand-dark/45">
          Textos del bloque «Diagnóstico Técnico» y del PDF, según TWA vs límites del protocolo.
        </p>
        <Field label="Nivel SEGURO (por debajo del nivel de acción)">
          <textarea
            className={textareaClass}
            value={form.ruido.diagnostics.seguro}
            onChange={(e) => setDiag({ seguro: e.target.value })}
          />
        </Field>
        <Field label="Nivel de ACCIÓN">
          <textarea
            className={textareaClass}
            value={form.ruido.diagnostics.accion}
            onChange={(e) => setDiag({ accion: e.target.value })}
          />
        </Field>
        <Field label="Nivel EXCEDIDO (criterio)">
          <textarea
            className={textareaClass}
            value={form.ruido.diagnostics.excedido}
            onChange={(e) => setDiag({ excedido: e.target.value })}
          />
        </Field>
        <Field label="Nota al pie (panel derecho)">
          <textarea
            className={textareaClass}
            value={form.ruido.footerNote}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                ruido: { ...prev.ruido, footerNote: e.target.value },
              }))
            }
          />
        </Field>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-6">
        <div>
          <h3 className="font-bold text-lg">Ruido — protocolos</h3>
          <p className="text-[11px] text-brand-dark/45 mt-1">
            Nombre y descripción visibles. Los umbrales dB siguen fijos en el motor de cálculo.
          </p>
        </div>
        {PROTOCOL_KEYS.map(({ id, label }) => (
          <div key={id} className="space-y-3 border-t border-brand-soft pt-4 first:border-0 first:pt-0">
            <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
              {label}
            </p>
            <Field label="Nombre en UI">
              <input
                className={inputClass}
                value={form.ruido.protocols[id].name}
                onChange={(e) => setProtocol(id, { name: e.target.value })}
              />
            </Field>
            <Field label="Descripción">
              <textarea
                className={textareaClass}
                value={form.ruido.protocols[id].desc}
                onChange={(e) => setProtocol(id, { desc: e.target.value })}
              />
            </Field>
          </div>
        ))}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Estrés térmico — etiquetas de riesgo</h3>
        <Field label="Seguro">
          <input
            className={inputClass}
            value={form.calor.riskLabels.seguro}
            onChange={(e) => setCalorRisk({ seguro: e.target.value })}
          />
        </Field>
        <Field label="Precaución">
          <input
            className={inputClass}
            value={form.calor.riskLabels.precaucion}
            onChange={(e) => setCalorRisk({ precaucion: e.target.value })}
          />
        </Field>
        <Field label="Peligro">
          <input
            className={inputClass}
            value={form.calor.riskLabels.peligro}
            onChange={(e) => setCalorRisk({ peligro: e.target.value })}
          />
        </Field>
      </section>

      <SaveBar saving={saving} onSave={onSave} />
    </div>
  );
}
